import { createMasterEQ, wireMasterEQ } from '../eqDsp';
import { createAnalyser, setAnalyserFftOrder } from './analyserDsp';
// Limiter/clipper DSP removed from master chain
import { ctxOk, safeConnectCtx, belongsToLiveCtx } from './bufferTransport/ctxUtil';
import { userGestureSeen, resumeCtxOnGesture } from './orchestrator/autoplayGate';

// Guard against duplicate wiring of the master chain (duplicate connections amplify audio)
const wiredOnce = new WeakSet<GainNode>();
// Guard against repeated connects from stem gain nodes to master/destination (can cause hiccups)
const connectedToMaster = new WeakSet<GainNode>();

// Re-entrancy guard for master chain rebuilds
let rebuilding = false;
let chainVersion = 0;

export function getMasterChainVersion() { return chainVersion; }

export function ensureMasterChain(
  ctx: AudioContext,
  refs: {
    masterGainRef: React.MutableRefObject<GainNode | null>;
    // Dedicated transport gate (volume + mute)
    gateGainRef?: React.MutableRefObject<GainNode | null>;
    // Passive tee for scopes/meters (pre-gate). If provided, we'll connect analysers here.
    teeGainRef?: React.MutableRefObject<GainNode | null>;
    masterHighpassRef: React.MutableRefObject<BiquadFilterNode | null>;
    masterFilterRef: React.MutableRefObject<BiquadFilterNode | null>;
    // Post-gate analyser (used for meters)
    masterAnalyserRef: React.MutableRefObject<AnalyserNode | null>;
    // Pre-gate analyser (used for health probe before opening transport gate)
    masterAnalyserPreRef?: React.MutableRefObject<AnalyserNode | null>;
    // DSP block removed: refs may exist but are ignored by wiring
    masterLimiterRef?: React.MutableRefObject<DynamicsCompressorNode | null>;
    masterClipperRef?: React.MutableRefObject<WaveShaperNode | null>;
    masterLookaheadLimiterRef?: React.MutableRefObject<AudioWorkletNode | null>;
  // optional bypass gain to avoid disconnects
  bypassGainRef?: React.MutableRefObject<GainNode | null>;
  },
  params: { masterVolume: number; masterFftOrder: number; masterHighpassHz: number; masterLowpassHz: number; q?: number },
  flags?: { eqEnabled?: boolean; eqHpfEnabled?: boolean; eqLpfEnabled?: boolean; freqResponseEnabled?: boolean }
): boolean {
  // Bail early if the context is closed to avoid connect warnings and no-op wiring
  if (!ctxOk(ctx)) {
    // console.debug('[graph] skip ensureMasterChain: ctx closed');
    return false;
  }
  const eqEnabled = flags?.eqEnabled !== false; // default true
  const eqHpfEnabled = flags?.eqHpfEnabled !== false; // default true
  const eqLpfEnabled = flags?.eqLpfEnabled !== false; // default true
  const freqResponseEnabled = flags?.freqResponseEnabled !== false; // default true
  // Recreate nodes that don't belong to the live context to avoid cross-context wiring
  if (!refs.masterGainRef.current || !belongsToLiveCtx(refs.masterGainRef.current)) {
    refs.masterGainRef.current = ctx.createGain();
  }

  const hpLive = !!refs.masterHighpassRef.current && belongsToLiveCtx(refs.masterHighpassRef.current);
  const lpLive = !!refs.masterFilterRef.current && belongsToLiveCtx(refs.masterFilterRef.current);
  if (!hpLive || !lpLive) {
    const { hp, lp } = createMasterEQ(ctx, { hpfHz: params.masterHighpassHz, lpfHz: params.masterLowpassHz, q: params.q ?? 0.707 });
    refs.masterHighpassRef.current = hp;
    refs.masterFilterRef.current = lp;
  }

  // DSP block removed: do not create limiter/clipper/lookahead worklet

  // create bypass gain if missing
  if (refs.bypassGainRef && (!refs.bypassGainRef.current || !belongsToLiveCtx(refs.bypassGainRef.current))) {
    refs.bypassGainRef.current = ctx.createGain();
  }

  // Create analysers (post for meters, pre for health probe)
  if (freqResponseEnabled) {
    if (!refs.masterAnalyserRef.current || !belongsToLiveCtx(refs.masterAnalyserRef.current)) {
      refs.masterAnalyserRef.current = createAnalyser(ctx, { fftOrder: params.masterFftOrder, smoothing: 0.17 });
    } else {
      setAnalyserFftOrder(refs.masterAnalyserRef.current, params.masterFftOrder);
    }
  }
  if (refs.masterAnalyserPreRef) {
    if (!refs.masterAnalyserPreRef.current || !belongsToLiveCtx(refs.masterAnalyserPreRef.current)) {
      refs.masterAnalyserPreRef.current = createAnalyser(ctx, { fftOrder: params.masterFftOrder, smoothing: 0.17 });
    } else {
      setAnalyserFftOrder(refs.masterAnalyserPreRef.current, params.masterFftOrder);
    }
  }

  // Keep nodes connected, but only wire the audio path once to avoid parallel edges.
  // Ensure gate exists (between mix bus and EQ)
  if (refs.gateGainRef && (!refs.gateGainRef.current || !belongsToLiveCtx(refs.gateGainRef.current))) {
    refs.gateGainRef.current = ctx.createGain();
    // Default the transport gate to OPEN to avoid deadlocks before any probe logic runs
    try {
      const now = ctx.currentTime;
      const g = refs.gateGainRef.current.gain;
      g.cancelScheduledValues(now);
      g.setValueAtTime(1, now);
    } catch {}
  }

  // Gain staging:
  // - If a dedicated gate exists, keep master at unity and apply volume on the gate
  // - If no gate, apply volume on master
  if (refs.gateGainRef?.current) {
    const anyRefs: any = refs as any;
    const isOpen = (anyRefs._gateState ?? 1) ? true : false;
    const targetWhenOpen = params.masterVolume;
    const effective = isOpen ? targetWhenOpen : 0;
    try { refs.masterGainRef.current!.gain.value = 1; } catch {}
    try {
      const g = refs.gateGainRef.current.gain;
      g.value = effective;
      // Persist desired open target so transport gate can re-open to this level
      anyRefs._gatePrevTarget = targetWhenOpen;
    } catch {}
  } else {
    try { refs.masterGainRef.current!.gain.value = params.masterVolume; } catch {}
  }

  // Compute a signature of topology + key params that require rewiring
  const useEqTap = !!(eqEnabled && (eqHpfEnabled || eqLpfEnabled));
  // Only include topology-affecting booleans in the signature so parameter tweaks don't cause rewires
  const sigObj = {
    hasGate: !!refs.gateGainRef?.current,
    hasBypass: !!refs.bypassGainRef?.current,
    useEqTap,
    hasTee: !!refs.teeGainRef?.current,
  } as const;
  let sig = '';
  try { sig = JSON.stringify(sigObj); } catch { sig = `${sigObj.hasGate}|${sigObj.hasBypass}|${sigObj.useEqTap}|${sigObj.hasTee}`; }

  const anyRefs: any = refs as any;
  if (anyRefs._masterSig === sig) {
    // Signature unchanged: always ensure analyser taps POST-FADER (after LPF),
    // so master visuals reflect gate/master volume regardless of EQ toggle.
    if (freqResponseEnabled && refs.masterAnalyserRef.current) {
      try { refs.masterGainRef.current!.disconnect(refs.masterAnalyserRef.current); } catch {}
      try { refs.masterFilterRef.current!.disconnect(refs.masterAnalyserRef.current); } catch {}
      // Prefer LPF (post-fader) > master for analyser tap
      const tapNode = (refs.masterFilterRef.current || refs.masterGainRef.current!);
      try { safeConnectCtx(ctx, tapNode, refs.masterAnalyserRef.current); } catch {}
    }
    anyRefs._masterTapUseEq = useEqTap;
    return false; // no rewiring
  }
  anyRefs._masterSig = sig;
  anyRefs._masterTapUseEq = useEqTap;

  // Enforce the single, explicit master chain order idempotently on every call:
  // masterGain → (analyserPre tap) → gateGain? → HPF → LPF → (bypass?) → destination
  const mg = refs.masterGainRef.current!;
  const gate = (refs.gateGainRef?.current ?? null) as GainNode | null;
  // Create tee if requested and missing
  let tee = (refs.teeGainRef?.current ?? null) as GainNode | null;
  if (refs.teeGainRef && (!tee || !belongsToLiveCtx(tee))) {
    const t = ctx.createGain();
    try { t.gain.value = 1; } catch {}
    refs.teeGainRef.current = t;
    tee = t;
  }
  try { (mg as any).disconnect?.(); } catch {}
  try { refs.masterHighpassRef.current && (refs.masterHighpassRef.current as any).disconnect?.(); } catch {}
  try { refs.masterFilterRef.current && (refs.masterFilterRef.current as any).disconnect?.(); } catch {}
  // No DSP nodes to disconnect
  try { gate && (gate as any).disconnect?.(); } catch {}
  try { tee && (tee as any).disconnect?.(); } catch {}

  // Optional passive tee from master gain for scopes/meters
  if (tee) {
    try { safeConnectCtx(ctx, mg, tee); } catch {}
  }
  if (gate) {
    try { safeConnectCtx(ctx, mg, gate); } catch {}
    try { safeConnectCtx(ctx, gate, refs.masterHighpassRef.current!); } catch {}
  } else {
    try { safeConnectCtx(ctx, mg, refs.masterHighpassRef.current!); } catch {}
  }
  try { safeConnectCtx(ctx, refs.masterHighpassRef.current!, refs.masterFilterRef.current!); } catch {}
  // DSP nodes removed
  if (refs.bypassGainRef && refs.bypassGainRef.current) {
    // lp -> bypass -> destination
    const start = refs.masterFilterRef.current!;
    let tail: AudioNode = start;
    try { safeConnectCtx(ctx, tail, refs.bypassGainRef.current); } catch {}
    try { safeConnectCtx(ctx, refs.bypassGainRef.current, ctx.destination); } catch {}
  } else {
    // lp -> destination
    const start = refs.masterFilterRef.current!;
    let tail: AudioNode = start;
    try { safeConnectCtx(ctx, tail, ctx.destination); } catch {}
  }

  // Optional pre-gate analyser tap should use tee when present to avoid touching mg directly
  if (refs.masterAnalyserPreRef?.current) {
  const tapSrc = tee ?? mg;
  try { safeConnectCtx(ctx, tapSrc, refs.masterAnalyserPreRef.current); } catch {}
  }

  // Always retap the analyser safely: disconnect old taps and connect the right node
  if (freqResponseEnabled && refs.masterAnalyserRef.current) {
    // Always tap POST-FADER: after LPF (which is after gate/master volume).
    try { refs.masterGainRef.current!.disconnect(refs.masterAnalyserRef.current); } catch {}
    try { refs.masterFilterRef.current!.disconnect(refs.masterAnalyserRef.current); } catch {}
    // Prefer LPF (post-fader) for analyser tap
    const tapNode = (refs.masterFilterRef.current || refs.masterGainRef.current!);
    try { safeConnectCtx(ctx, tapNode, refs.masterAnalyserRef.current); } catch {}
  }
  return true; // rewired
}

// Idempotent wrapper with rebuild guard to prevent overlapping rewires
export function ensureMasterChainIdempotent(
  ctx: AudioContext,
  refs: Parameters<typeof ensureMasterChain>[1],
  params: Parameters<typeof ensureMasterChain>[2],
  flags?: Parameters<typeof ensureMasterChain>[3]
): boolean {
  if (!ctxOk(ctx)) return false;
  if (rebuilding) return false;
  rebuilding = true;
  try {
    const startVer = chainVersion;
    const rewired = ensureMasterChain(ctx, refs as any, params as any, flags as any);
    chainVersion = startVer + (rewired ? 1 : 0);
    // Rehydrate gate to persisted state after any chain touch
    try {
      const anyRefs: any = refs as any;
      const gate: GainNode | null = (anyRefs.gateGainRef?.current ?? null) as any;
      if (gate) {
        const ctx2: AudioContext | undefined = (anyRefs.audioCtxRef?.current ?? ctx) as any;
        const now = ctx2?.currentTime ?? 0;
        const isOpen = (anyRefs._gateState ?? 1) ? true : false;
        const openTarget = anyRefs._gatePrevTarget ?? params.masterVolume;
        const effective = isOpen ? openTarget : 0;
        try { gate.gain.cancelScheduledValues(now); } catch {}
        try { gate.gain.setValueAtTime(effective, now); } catch { try { (gate.gain as any).value = effective; } catch {} }
      }
    } catch {}
    return rewired;
  } finally {
    rebuilding = false;
  }
}

// Create and attach a passive analyser to the mix bus tee without touching the live master path.
// Returns the created AnalyserNode.
export function attachScopeAnalyser(
  ctx: AudioContext,
  refs: {
    teeGainRef?: React.MutableRefObject<GainNode | null>;
    masterGainRef: React.MutableRefObject<GainNode | null>;
  },
  options?: { fftOrder?: number; smoothing?: number }
) {
  const an = createAnalyser(ctx, { fftOrder: options?.fftOrder ?? 11, smoothing: options?.smoothing ?? 0.8 });
  const tap = refs.teeGainRef?.current ?? refs.masterGainRef.current;
  if (tap && ctxOk(ctx)) {
    try { safeConnectCtx(ctx, tap, an); } catch {}
  }
  return an;
}

// Retap only the master analyser without altering LPF/HPF -> destination wiring
export function retapMasterAnalyser(
  refs: any,
  flags: { eqEnabled?: boolean; eqHpfEnabled?: boolean; eqLpfEnabled?: boolean }
) {
  const an: AnalyserNode | null = refs.masterAnalyserRef?.current ?? null;
  const mg: GainNode | null = refs.masterGainRef?.current ?? null;
  const lp: BiquadFilterNode | null = refs.masterFilterRef?.current ?? null;
  const lim: DynamicsCompressorNode | null = refs.masterLimiterRef?.current ?? null;
  const clip: WaveShaperNode | null = refs.masterClipperRef?.current ?? null;
  try {
    const ctx = (mg?.context ?? lp?.context) as AudioContext | undefined;
    if (ctx && !ctxOk(ctx)) return;
  } catch {}
  if (!an || !mg) return;
  try { mg.disconnect(an); } catch {}
  try { lp?.disconnect(an); } catch {}
  try { lim?.disconnect(an); } catch {}
  try { clip?.disconnect(an); } catch {}
  // Prefer final output: clipper > limiter > LPF > master gain
  const tapNode = clip || lim || lp || mg;
  try {
    const c = ((tapNode as any)?.context || (an as any)?.context) as AudioContext | undefined;
    if (c) safeConnectCtx(c, tapNode, an);
  } catch {}
}

// Belt-and-suspenders: ensure the final hop to destination is alive and context is running
export function ensureDestinationAlive(
  ctx: AudioContext,
  refs: {
    masterGainRef: React.MutableRefObject<GainNode | null>;
    masterHighpassRef: React.MutableRefObject<BiquadFilterNode | null>;
    masterFilterRef: React.MutableRefObject<BiquadFilterNode | null>;
  },
) {
  if (!ctxOk(ctx)) return;
  // 1) Ensure context is running
  // Only try to resume the AudioContext after a user gesture to avoid autoplay warnings
  try {
    if (ctx.state !== 'running') {
      if (userGestureSeen()) {
        void ctx.resume().catch(() => {});
      } else {
        // Defer resume until the first gesture; don't log warnings pre-gesture
        void resumeCtxOnGesture(ctx);
      }
    }
  } catch {}
  // Do not disconnect or reconnect the master chain here; that risks muting.
  // Master wiring is handled once in ensureMasterChain via a wiredOnce guard.
}

export function ensureStemChain(
  ctx: AudioContext,
  role: string,
  el: HTMLAudioElement,
  effectiveGain: number,
  dicts: {
    sourcesRef: React.MutableRefObject<Record<string, MediaElementAudioSourceNode>>;
    gainsRef: React.MutableRefObject<Record<string, GainNode>>;
    analyzersRef: React.MutableRefObject<Record<string, AnalyserNode>>;
  delaysRef: React.MutableRefObject<Record<string, DelayNode>>;
    connectedElsRef: React.MutableRefObject<Record<string, HTMLAudioElement | null>>;
  },
  masterGain: GainNode | null
) {
  if (!ctxOk(ctx)) { return; }
  // Ensure inline playback and mute native path to avoid double audio (we drive audio via WebAudio)
  try { (el as any).playsInline = true; } catch {}
  try { el.muted = true; } catch {}
  try { el.volume = 0; } catch {}
  const prevEl = dicts.connectedElsRef.current[role];
  if (prevEl && prevEl !== el) {
    try { dicts.sourcesRef.current[role]?.disconnect(); } catch {}
    try { dicts.gainsRef.current[role]?.disconnect(); } catch {}
    try { dicts.analyzersRef.current[role]?.disconnect(); } catch {}
  try { dicts.delaysRef.current[role]?.disconnect(); } catch {}
    delete dicts.sourcesRef.current[role];
    delete dicts.gainsRef.current[role];
    delete dicts.analyzersRef.current[role];
  delete dicts.delaysRef.current[role];
    dicts.connectedElsRef.current[role] = null;
  }

  if (!dicts.sourcesRef.current[role]) {
    let srcNode: MediaElementAudioSourceNode | null = null;
    try {
      srcNode = ctx.createMediaElementSource(el);
    } catch (err) {
      // Do not fall back to captureStream to avoid jittery glitches; disable this stem instead.
      try { el.muted = true; } catch {}
      dicts.connectedElsRef.current[role] = el;
      try { console.warn(`[mix] '${role}': WebAudio source not available; disabling stem to avoid glitches.`); } catch {}
      return; // skip WebAudio wiring entirely
    }
    const g = ctx.createGain();
    g.gain.value = effectiveGain;
  const delay = ctx.createDelay(0.1); // up to 100 ms micro-alignment
  try { delay.delayTime.value = 0; } catch {}
  const an = createAnalyser(ctx, { smoothing: 0.17 });
  // wire: src -> delay -> analyser ; src -> delay -> gain -> master/dest
  try { (srcNode as any).connect(delay); } catch {}
  try { safeConnectCtx(ctx, delay, an); } catch {}
  try { safeConnectCtx(ctx, delay, g); } catch {}
  if (masterGain) {
    if (!connectedToMaster.has(g)) {
  try { if (belongsToLiveCtx(masterGain)) { safeConnectCtx(ctx, g, masterGain); connectedToMaster.add(g); } } catch {}
    }
  } else {
    // fallback to destination only once to avoid duplicate routes
    if (!connectedToMaster.has(g)) {
  try { safeConnectCtx(ctx, g, ctx.destination); connectedToMaster.add(g); } catch {}
    }
  }
  // Store the MediaElementAudioSourceNode
  dicts.sourcesRef.current[role] = srcNode as MediaElementAudioSourceNode;
    dicts.gainsRef.current[role] = g;
    dicts.analyzersRef.current[role] = an;
  dicts.delaysRef.current[role] = delay;
    dicts.connectedElsRef.current[role] = el;
  } else {
    dicts.gainsRef.current[role]!.gain.value = effectiveGain;
    // Avoid repeated connect() calls which may cause tiny hiccups in some browsers
    if (masterGain) {
      const g = dicts.gainsRef.current[role];
      if (g && !connectedToMaster.has(g)) {
        try { if (belongsToLiveCtx(masterGain)) { safeConnectCtx(ctx, g, masterGain); connectedToMaster.add(g); } } catch {}
      }
    }
  }
}

export function pruneOrphanStems(
  rolesNow: Set<string>,
  dicts: {
    sourcesRef: React.MutableRefObject<Record<string, MediaElementAudioSourceNode>>;
    gainsRef: React.MutableRefObject<Record<string, GainNode>>;
    analyzersRef: React.MutableRefObject<Record<string, AnalyserNode>>;
    delaysRef: React.MutableRefObject<Record<string, DelayNode>>;
    connectedElsRef: React.MutableRefObject<Record<string, HTMLAudioElement | null>>;
    elsRef: React.MutableRefObject<Record<string, HTMLAudioElement | null>>;
  }
) {
  for (const role of Object.keys(dicts.sourcesRef.current)) {
    if (!rolesNow.has(role)) {
      try { dicts.sourcesRef.current[role]?.disconnect(); } catch {}
      try {
        const g = dicts.gainsRef.current[role];
        g?.disconnect();
        if (g) { try { connectedToMaster.delete(g); } catch {} }
      } catch {}
      try { dicts.analyzersRef.current[role]?.disconnect(); } catch {}
      try { dicts.delaysRef.current[role]?.disconnect(); } catch {}
      delete dicts.sourcesRef.current[role];
      delete dicts.gainsRef.current[role];
      delete dicts.analyzersRef.current[role];
      delete dicts.delaysRef.current[role];
      delete dicts.elsRef.current[role];
      delete dicts.connectedElsRef.current[role];
    }
  }
}
